import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { GeneralDialog } from 'sharedComponents';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import { Row, Col } from 'react-bootstrap';

import Chart from './Chart';
import useCustomZoom from './useCustomZoom';
import useCustomizedOptions from './useCustomizedOptions';
import useChartInstance from './useChartInstance';

import './styles.scss';
import { translate } from 'helpers/translateHelper';
import { DefaultHeader } from './headers';
import { saveSeriesSelection, updateZoomLevel } from './helpers';
import { apiCallStatus } from 'helpers/constants';
import Chart3D from './Chart3D';
import EChart3D from './Echart3D';

const PopOutChart = (props) => {
    const [showChartPopout, setChartPopOut] = useState(false);
    const [uniqueScaling, setUniqueScaling] = useState(false);
    const [isHideDownload, setIsHideDownload] = useState(false);
    const [mainChartInstance, mainChartCallback, mainChartDestroyCallback] = useChartInstance(props.callback);
    const [popoutChartInstance, popOutChartCallback, popOutChartDestroyCallback] = useChartInstance(props.callback);
    const [zooming, setZooming] = useState();
    const zoomingStatus = zooming ? apiCallStatus.LOADING : apiCallStatus.SUCCESS;

    const [selectionCallbackFn] = useCustomZoom(
        props,
        mainChartInstance,
        popoutChartInstance,
        showChartPopout,
        props.zoomOut || ((chartInst) => chartInst?.zoomOut()),
        setZooming
    );

    const [chartOptions] = useCustomizedOptions(props.options, props.enableUniqueScaling, uniqueScaling, selectionCallbackFn);

    useEffect(() => {
        if (!props?.hasData && props?.renderNoData) {
            setIsHideDownload(true);
        } else {
            setIsHideDownload(false);
        }
    }, [props?.hasData, props?.renderNoData]);

    // Notify parent with chart instance when it changes only for 3D charts
    useEffect(() => {
        if (props.onChartReady) {
            if (showChartPopout && popoutChartInstance) {
                props.onChartReady(popoutChartInstance);
            } else if (mainChartInstance) {
                props.onChartReady(mainChartInstance);
            }
        }
    }, [mainChartInstance, popoutChartInstance, props, showChartPopout]);

    // Comment out due to y axis min and max problems in FFT 2D chart
    // useEffect(() => {
    //     let pollTimer;
    //     if (
    //         showChartPopout &&
    //         mainChartInstance &&
    //         popoutChartInstance &&
    //         props?.options
    //     ) {
    //         // Support both array and object forms for xAxis/yAxis
    //         const getAxis = (chart, axisType) => {
    //             if (!chart) { return null; }
    //             if (Array.isArray(chart[axisType])) { return chart[axisType][0]; }
    //             return chart[axisType];
    //         };

    //         let attempts = 0;
    //         pollTimer = setInterval(() => {
    //             attempts++;
    //             const mainXAxis = getAxis(mainChartInstance, 'xAxis');
    //             const mainYAxis = getAxis(mainChartInstance, 'yAxis');
    //             const popoutXAxis = getAxis(popoutChartInstance, 'xAxis');
    //             const popoutYAxis = getAxis(popoutChartInstance, 'yAxis');
    //             if (mainXAxis && popoutXAxis) {
    //                 const xExtremes = mainXAxis.getExtremes();
    //                 popoutXAxis.setExtremes(xExtremes.min, xExtremes.max);
    //             }
    //             if (mainYAxis && popoutYAxis) {
    //                 const yExtremes = mainYAxis.getExtremes();
    //                 popoutYAxis.setExtremes(yExtremes.min, yExtremes.max);
    //             }
    //             // Sync signal visibility from main to popout
    //             saveSeriesSelection(popoutChartInstance, mainChartInstance);

    //             // Stop once we have synced successfully or after a few attempts
    //             if (mainXAxis && popoutXAxis || attempts > 20) {
    //                 clearInterval(pollTimer);
    //             }
    //         }, 50);
    //     }
    //     return () => {
    //         if (pollTimer) { clearInterval(pollTimer); }
    //     };
    // }, [showChartPopout, mainChartInstance, popoutChartInstance, props?.options]);

    const renderChart = (container, callbackFn, destroyFn) => {
        if (!props.hasKpi && props.renderNoKpi) {
            return props.renderNoKpi();
        }

        if (!props.hasData && props.renderNoData) {
            return props.renderNoData();
        }

        const chartProps = {
            options: chartOptions,
            container,
            modules: props.modules,
            callback: callbackFn,
            destroy: destroyFn,
            ...props.isEchart3D ? {
                options2D: props.options2D,
                on2DEvents: props.on2DEvents,
                chart2DRef: props.chart2DRef,
            } : {},
            ...props.isTrendSignal ? { isTrendSignal: props.isTrendSignal, showChartPopout } : {},
            ...props.setChartLoader ? { setChartLoader: props.setChartLoader } : {},
            ...props.chartRef && !showChartPopout ? { ref: props.chartRef } : {}
        };

        return props.isFor3dChart
            ? <Chart3D {...chartProps} />
            : props.isEchart3D
                ? <EChart3D {...chartProps} />
                : <Chart {...chartProps} />;
    };

    const MainChartHeader = props.mainChartHeader || DefaultHeader;
    const PopOutChartHeader = props.popOutChartHeader ? props.popOutChartHeader : MainChartHeader;

    return <>
        <MainChartHeader
            {...props}
            mainChartInst={mainChartInstance}
            uniqueScaling={uniqueScaling}
            setUniqueScaling={setUniqueScaling}
            chartOptions={chartOptions}
            showChartPopout={showChartPopout}
            setChartPopOut={setChartPopOut}
            labelLastSync={props.labelLastSync || translate('ABB.Powertrain.Frontend.labelLastSync')}
            setZooming={setZooming}
            isHideDownload={isHideDownload}
        />
        {props.mainChartBanner || null}
        <Row className='popout-chart-row chart-wrapper-row'>
            <Col>
                <div className='chart-container'>
                    {renderChart(props.container, mainChartCallback, mainChartDestroyCallback)}
                    {props.isFor3dChart && props.popOutFooter}
                    {props.isEchart3D && props.popOutFooter}
                    {props.isEchart3D && props.echartPanels}
                </div>
            </Col>
        </Row>
        <GeneralDialog
            modalClassName={`popout-chart-modal${props.popOutModalClassName ? ' ' + props.popOutModalClassName : ''}`}
            show={showChartPopout}
            close={() => {
                if (props.loadingStatus && props.loadingStatus !== apiCallStatus.LOADING || props.memorizePopoutChart) {
                    saveSeriesSelection(mainChartInstance, popoutChartInstance);
                    updateZoomLevel(mainChartInstance, popoutChartInstance, props.data);
                }
                setChartPopOut(false);
            }}
            closeButton={true}
        >
            <div
                className={`chart-container popout-chart-container${props.popOutClassName ? ` ${props.popOutClassName}` : ''}`}
                style={props.popOutContainerCustomStyle}
            >
                <LoadingStatusWrapper useCustomAnchor loadingStatus={[props.loadingStatus, zoomingStatus]} transparent>
                    <PopOutChartHeader
                        {...props}
                        mainChartInst={popoutChartInstance}
                        chartOptions={chartOptions}
                        showChartPopout={showChartPopout}
                        setChartPopOut={setChartPopOut}
                        uniqueScaling={uniqueScaling}
                        setUniqueScaling={setUniqueScaling}
                        hideSelect={true}
                        labelLastSync={props.labelLastSync || translate('ABB.Powertrain.Frontend.labelLastSync')}
                    />
                    {renderChart(`${props.container}-pop-out`, popOutChartCallback, popOutChartDestroyCallback)}
                    {props.popOutFooter}
                    {props.isEchart3D && props.echartPanels}
                </LoadingStatusWrapper>
            </div>
        </GeneralDialog>
    </>;
};

PopOutChart.propTypes = {
    callback: PropTypes.func,
    container: PropTypes.any,
    hasKpi: PropTypes.bool,
    loadingStatus: PropTypes.string,
    modules: PropTypes.array,
    options: PropTypes.object,
    renderNoKpi: PropTypes.func,
    setIsZoomed: PropTypes.func.isRequired,
    isZoomed: PropTypes.bool.isRequired,
    zoomInServer: PropTypes.func,
    resetServerZoom: PropTypes.func,
    enableUniqueScaling: PropTypes.bool,
    hasData: PropTypes.bool,
    renderNoData: PropTypes.func,
    memorizePopoutChart: PropTypes.bool,
    isTrendSignal: PropTypes.bool,
    isVibrationFFT2D: PropTypes.bool,
    mainChartBanner: PropTypes.node,
    isNotPreserveSignals: PropTypes.bool,
    useLocalTimeZoom: PropTypes.bool,
    popOutModalClassName: PropTypes.string,
};

export default PopOutChart;
